import React, { useState } from 'react';

// components
import { PreviewBoxComponent } from 'baseComponents/PreviewBoxComponent';
import { TextComponent } from 'baseComponents/TextComponent';
import { ButtonComponent } from 'baseComponents/ButtonComponent';
import { DropdownSelectComponent } from 'baseComponents/DropdownSelectComponent';
import { InputComponent } from 'baseComponents/InputComponent';
import { LinkComponent } from 'baseComponents/LinkComponent';
import { MultiSelectDropdownComponent } from 'baseComponents/MultiSelectDropdownComponent';

// screens
import { ReferenceTablesModal } from 'screens/CreateCampaignScreen/components/ReferenceTablesModal';
import { TabContentWrapper } from 'screens/CreateCampaignScreen/components/TabContentWrapper';

// helpers
import { parseMessagesTemplates, parseTestUsers } from 'helpers/dropdown-data';

// styles
import './composeTab.scss';

// constants
import { CREATE_CAMPAIGN_SCREEN } from 'constants/enums/create-campaign-screen';

// hooks
import { useGetReferenceTables } from 'hooks/useGetReferenceTables';
import {
  MessageTemplateType,
  useGetMessagesTemplates,
} from 'hooks/useGetMessagesTemplates';
import { useGetTestUsers } from 'hooks/useGetTestUsers';
import { useSendTestMessage } from 'hooks/useSendTestMessage';

// store
import {
  useCreateCampaignStore,
  useCreateCampaignStoreActions,
} from 'store/CreateCampaignStore';

// helpers
import {
  campaignMessagePayload,
  replaceBreakLine,
  replaceUrl,
} from 'helpers/create-campaign';
import { useGetBlankTemplate } from 'hooks/useGetBlankTemplate';
import { extractAfterTripleBraces } from 'helpers/compose-message';
import { notification } from 'antd';
import { FullScreenLoaderComponent } from 'baseComponents/FullScreenLoaderComponent';

export const ComposeTab: React.FC = () => {
  // zustand store
  const {
    setMessage: setCampaignMessage,
    setMessageTemplate: setCampaignMessageTemplate,
    setMessageEncoding: setCampaignMessageEncoding,
    setPreviewMessage: setCampaignPreviewMessage,
    setMessagesQuantity: setCampaignMessagesQuantity,
  } = useCreateCampaignStoreActions();

  const campaignMessage = useCreateCampaignStore((state) => state.message);
  const campaignPreviewMessage = useCreateCampaignStore(
    (state) => state.previewMessage
  );
  const campaignMessagesQuantity = useCreateCampaignStore(
    (state) => state.messagesQuantity
  );
  const campaignMessageTemplate = useCreateCampaignStore(
    (state) => state.messageTemplate
  );

  // local store
  const [isModalOpen, setModalOpen] = useState(false);
  const [composeFromTemplate, setComposeFromTemplate] =
    useState<boolean>(false);
  const [testUsers, setTestUsers] = useState<string[]>([]);
  const [api, contextHolder] = notification.useNotification();

  const handleComposeFromTemplate = (item: MessageTemplateType) => {
    setCampaignMessage('');
    setCampaignMessageTemplate(item);
    setComposeFromTemplate(true);
    setCampaignPreviewMessage(replaceUrl(item.message));
    countMessages(replaceUrl(item.message));
  };

  const {
    error: messagesTemplatesError,
    data: messagesTemplatesData,
    isLoading: isLoadingTemplates,
  } = useGetMessagesTemplates('sms');

  const { data: blankTemplateData, refetch: refetchBlankTemplate } =
    useGetBlankTemplate();

  const {
    data: testUsersData,
    isLoading: isLoadingTestUsers,
    isRefetching: isRefetchingTestUsers,
    isError: isTestUsersError,
  } = useGetTestUsers('sms');

  const {
    isLoading: referenceTablesLoading,
    error: referenceTablesError,
    data: referenceTablesData,
  } = useGetReferenceTables({
    enabled: isModalOpen,
  });

  const countMessages = (text: string) => {
    // count characters
    const totalCharacters = text.length;

    // check if there are any characters that require UCS2 encoding
    let requiresUCS2 = false;
    for (let i = 0; i < text.length; i++) {
      // @ts-ignore
      if (text.codePointAt(i) > 0xffff) {
        requiresUCS2 = true;
        break;
      }
    }

    // count credits based on encoding
    if (requiresUCS2) {
      let messagesUCS2 =
        totalCharacters <= 70
          ? Math.ceil(totalCharacters / 70)
          : Math.ceil(totalCharacters / 67);
      setCampaignMessageEncoding('UCS-2');
      setCampaignMessagesQuantity(messagesUCS2.toString());
    } else {
      let messagesGSM7 =
        totalCharacters <= 160
          ? Math.ceil(totalCharacters / 160)
          : Math.ceil(totalCharacters / 153);
      setCampaignMessageEncoding('GSM-7');
      setCampaignMessagesQuantity(messagesGSM7.toString());
    }
  };

  const composeMsg = (value: string) => {
    if (blankTemplateData && blankTemplateData.length > 0) {
      return replaceUrl(
        `${value}\n\n${extractAfterTripleBraces(blankTemplateData[0].message)}`
      );
    }
    return replaceUrl(value);
  };

  const handleMessageTextarea = (e: any) => {
    setCampaignMessage(e.target.value);
    setCampaignPreviewMessage(composeMsg(replaceUrl(e.target.value)));
    countMessages(composeMsg(replaceUrl(e.target.value)));
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleComposeNew = () => {
    setCampaignMessageTemplate(null);
    setComposeFromTemplate(false);
    refetchBlankTemplate();
    setCampaignPreviewMessage(
      blankTemplateData && blankTemplateData.length > 0
        ? `\n\n${extractAfterTripleBraces(blankTemplateData[0].message)}`
        : ''
    );
  };

  const dropdownLabel = !composeFromTemplate
    ? CREATE_CAMPAIGN_SCREEN.TEMPLATES
    : campaignMessageTemplate!.name;

  const messageValue = composeFromTemplate
    ? campaignMessageTemplate!.message
    : campaignMessage;

  const handleSelectTestUsers = (item: any) => {
    setTestUsers(item);
  };

  const sendTestMessage = useSendTestMessage({
    onSuccess: () => {
      return api.success({
        message: 'Test message has been sent!',
        description: 'Test message has been sent to test users.',
        duration: 3,
      });
    },
    onError: (data) => {
      return api.error({
        message: data?.message || 'Something went wrong',
        description:
          'Something went wrong sending test message. Please try again later.',
        duration: 3,
      });
    },
  });

  const handleSendToTestUsers = async () => {
    const campaignMessageId = campaignMessageTemplate
      ? campaignMessageTemplate.campaign_id
      : null;
    const messagePayload = campaignMessagePayload(
      campaignMessage,
      campaignMessageId,
      null
    );
    sendTestMessage.mutate({
      message: messagePayload,
      test_user_ids: testUsers.map((str) => parseInt(str)),
      channel: 'sms',
    });
  };

  const parsedMessagesTemplates = messagesTemplatesData
    ? parseMessagesTemplates(messagesTemplatesData, handleComposeFromTemplate)
    : [];

  const parsedTestUsers = () => {
    return parseTestUsers(testUsersData || []);
  };

  return (
    <TabContentWrapper tabTitle='Compose'>
      {contextHolder}
      <div className='compose-sms'>
        <div className='compose-left-column'>
          <PreviewBoxComponent
            label={CREATE_CAMPAIGN_SCREEN.LIVE_PREVIEW}
            previewMessage={campaignPreviewMessage}
            hardcodedHeight
          />
        </div>
        <div className='compose-right-column'>
          <div className='compose-buttons'>
            <ButtonComponent
              onClick={handleComposeNew}
              label={CREATE_CAMPAIGN_SCREEN.COMPOSE_NEW}
              uppercase
              appereance='primary'
              size='middle'
            />
            {!messagesTemplatesError && (
              <DropdownSelectComponent
                selectLabelValue={dropdownLabel}
                items={parsedMessagesTemplates || []}
                loading={isLoadingTemplates}
              />
            )}
          </div>
          <div className='compose-textarea'>
            <TextComponent
              text={CREATE_CAMPAIGN_SCREEN.MESSAGE}
              weight='extra-light'
            />
            <InputComponent
              textarea
              value={messageValue}
              onChange={handleMessageTextarea}
              disabled={composeFromTemplate}
            />
            <div className='message-summary'>
              <div className='summary-row'>
                <TextComponent
                  weight='extra-bold'
                  text={CREATE_CAMPAIGN_SCREEN.CHARACTER_COUNT}
                />
                <TextComponent
                  weight='extra-light'
                  text={
                    campaignPreviewMessage
                      ? replaceBreakLine(
                          campaignPreviewMessage
                        ).length.toString()
                      : '0'
                  }
                />
              </div>
              <div className='summary-row'>
                <LinkComponent
                  label={CREATE_CAMPAIGN_SCREEN.BILLABLE_CREDITS}
                  onClick={handleModalOpen}
                />
                <TextComponent
                  weight='extra-light'
                  text={campaignMessagesQuantity}
                />
              </div>
            </div>
            {!isTestUsersError && (
              <div className='send-to-test-users'>
                {!isLoadingTestUsers &&
                !isRefetchingTestUsers &&
                parsedTestUsers().length === 0 ? (
                  <TextComponent text='No test users available' />
                ) : (
                  <>
                    <div>
                      <TextComponent
                        text={CREATE_CAMPAIGN_SCREEN.TEST_USER}
                      />
                      <MultiSelectDropdownComponent
                        options={parsedTestUsers()}
                        placeholder={CREATE_CAMPAIGN_SCREEN.SELECT_TEST_USER}
                        onChange={handleSelectTestUsers}
                        value={testUsers}
                      />
                    </div>
                    <ButtonComponent
                      label={CREATE_CAMPAIGN_SCREEN.SEND_TO_TEST_USERS}
                      onClick={handleSendToTestUsers}
                      size='large'
                      appereance='primary'
                      fullWidth
                      uppercase
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {!referenceTablesLoading &&
        !referenceTablesError &&
        referenceTablesData && (
          <ReferenceTablesModal
            closeModal={() => setModalOpen(false)}
            isModalOpen={isModalOpen}
            referenceTablesData={referenceTablesData}
          />
        )}
      <FullScreenLoaderComponent
        spinning={
          isLoadingTestUsers || isRefetchingTestUsers || referenceTablesLoading
        }
      />
    </TabContentWrapper>
  );
};
